/**
 * AuthHeader Component
 * 
 * Displays authentication UI in the top-right corner.
 * Works with both real Clerk auth and development mock auth.
 */

import React from 'react';
import {
  SignedIn,
  SignedOut,
  SignInButton,
  SignUpButton,
  UserButton,
} from '@clerk/clerk-react';
import { User, Home } from 'lucide-react';
import { IS_DEV_MODE } from '@/app/providers/AuthProvider';
import { FeedbackButton } from '@/app/components/feedback/FeedbackButton';
import { Button } from '@/app/components/ui/button';
import { LanguageSwitcher } from '@/app/components/LanguageSwitcher';
import { useLanguage } from '@/app/contexts/LanguageContext';

// Mock user for dev mode
const MOCK_USER = {
  name: 'Dev User',
  email: 'dev@example.com',
};

interface AuthHeaderProps {
  onShowLandingPage?: () => void;
}

export function AuthHeader({ onShowLandingPage }: AuthHeaderProps) {
  const { t } = useLanguage();

  // Development mode: Show mock user info
  if (IS_DEV_MODE) {
    return (
      <div className="absolute top-4 right-4 z-40 flex items-center gap-2">
        <LanguageSwitcher />
        {onShowLandingPage && (
          <Button 
            variant="outline" 
            size="icon" 
            className="bg-white border-slate-200 text-slate-600 hover:text-purple-600 shadow-sm"
            onClick={onShowLandingPage}
            title={t('common.toLandingPage')}
          >
            <Home className="w-4 h-4" />
          </Button>
        )}
        <FeedbackButton />
        <div className="flex items-center gap-3 bg-white rounded-lg shadow-md px-4 py-2 border border-amber-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
              <User className="w-5 h-5 text-amber-600" />
            </div>
            <div className="text-sm">
              <div className="font-medium text-gray-900">{MOCK_USER.name}</div>
              <div className="text-xs text-gray-500">{MOCK_USER.email}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Production mode: Use Clerk components
  return (
    <div className="absolute top-4 right-4 z-40 flex items-center gap-2">
      <LanguageSwitcher />
      <SignedOut>
        <FeedbackButton />
        <div className="flex items-center gap-2 bg-white rounded-lg shadow-md p-2">
          <SignInButton mode="modal">
            <button className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors">
              {t('common.signIn')}
            </button>
          </SignInButton>
          <SignUpButton mode="modal">
            <button className="px-4 py-2 border border-purple-600 text-purple-600 rounded hover:bg-purple-50 transition-colors">
              {t('common.signUp')}
            </button>
          </SignUpButton>
        </div>
      </SignedOut>
      
      <SignedIn>
        <FeedbackButton />
        <div className="flex items-center gap-3 bg-white rounded-lg shadow-md p-2">
          <UserButton
            appearance={{
              elements: {
                avatarBox: 'w-8 h-8',
              },
            }}
            showName
          />
        </div>
      </SignedIn>
    </div>
  );
}
